import { FeedbackFormData } from '../../components/feedback-form/feedback-types';
import { maxArrayLength } from '../../utils/validators/maxArrayLength';

export const wildOatsMappingFormData: FeedbackFormData = {
  id: 'wild-oats-mapping',
  title: $localize`We'd like to hear some more`,
  subtitle: $localize`We promise it will only take a few minutes of your time, and your answers are of high value for us`,
  questions: [
    [
      {
        id: 'statements',
        title: $localize`To what extent do you agree with the following statements?`,
        type: 'radio-block',
        options: [
          { id: 'remove', label: $localize`It is of high importance to remove wild oats from my fields` },
          { id: 'control', label: $localize`It is a challenge to control wild oats` },
          { id: 'location', label: $localize`Knowing the exact locations of wild oats would make planning easier` },
          { id: 'detect', label: $localize`A tool for detection and logging of wild oats would improve my yield` },
          { id: 'quality', label: $localize`A tool like this would improve crop quality` },
          { id: 'costs', label: $localize`A tool like this would reduce costs` },
          { id: 'time', label: $localize`Using a tool for detecting wild oats would save time` },
          { id: 'support', label: $localize`This tool would support sustainable and targeted weed control` },
        ],
      },
      {
        id: 'why-use',
        title: $localize`Why would or wouldn't you use a tool like this?`,
        subtitle: $localize`Please add a comment below`,
        type: 'textarea',
      },
    ],
    [
      {
        id: 'spot',
        title: $localize`How do you currently spot wild oats?`,
        subtitle: $localize`Select one or more answers`,
        type: 'check-block',
        options: [
          { id: 'visual', label: $localize`I visually inspect the fields while walking or driving through them` },
          { id: 'workers', label: $localize`I rely on field workers or agronomists to report sightings` },
          { id: 'drone', label: $localize`I use drone imagery` },
          { id: 'satellite', label: $localize`I use satellite imagery` },
          { id: 'harvest', label: $localize`I spot them during harvest` },
          { id: 'none', label: $localize`I don't actively look for wild oats` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`Please specify`,
        },
      },
      {
        id: 'how-control',
        title: $localize`How do you currently control wild oats in your fields?`,
        subtitle: $localize`Select one or more answers`,
        type: 'check-block',
        options: [
          { id: 'manual', label: $localize`I manually remove wild oats` },
          { id: 'area', label: $localize`I use spot spraying to treat affected areas` },
          { id: 'field', label: $localize`I manage wild oats by spraying the entire field` },
          { id: 'rotate', label: $localize`I rotate crops to reduce wild oats pressure` },
          { id: 'none', label: $localize`I don't currently control wild oats` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`Please specify`,
        },
      },
    ],
    [
      {
        id: 'when-contol',
        title: $localize`When do you currently take action to control wild oats?`,
        subtitle: $localize`Select the answer that best applies to your current practice`,
        type: 'radio',
        options: [
          { id: 'early', label: $localize`During early crop growth` },
          { id: 'mid-season', label: $localize`Mid-season` },
          { id: 'before-harvest', label: $localize`Just before harvest` },
          { id: 'after-harvest', label: $localize`After harvest` },
          { id: 'year-round', label: $localize`Year-round monitoring and control` },
          { id: 'none', label: $localize`I don't actively control wild oats` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`Please specify`,
        },
      },
      {
        id: 'current-practice',
        title: $localize`What do you typically do if you spot wild oats during harvest?`,
        subtitle: $localize`Select one or more answers`,
        type: 'check-block',
        options: [
          { id: 'stop', label: $localize`Stop harvesting and remove the wild oats manually before continuing` },
          { id: 'flag', label: $localize`Flag the area and return later after wild oats have been removed` },
          { id: 'continue', label: $localize`Continue harvesting and deal with wild oats later` },
          { id: 'separate', label: $localize`Separate and store affected yield apart from the clean yield` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`Please specify`,
        },
      },
    ],
    [
      {
        id: 'value',
        title: $localize`What would be the most valuable uses of a wild oat detection and logging tool?`,
        subtitle: $localize`Select up to 3 answers`,
        type: 'check-block',
        options: [
          { id: 'support', label: $localize`Supporting spot spraying or targeted treatment` },
          { id: 'improve', label: $localize`Improving crop planning and rotation strategies` },
          { id: 'monitor', label: $localize`Monitoring weed pressure over time` },
          { id: 'maps', label: $localize`Generating maps for field scouting` },
          { id: 'sorting', label: $localize`Sorting clean and affected yield during harvest` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: $localize`Please specify`,
        },
        validators: [maxArrayLength(3)],
      },
      {
        id: 'other-weeds',
        title: $localize`Would it be relevant for you if the tool could detect other weed types besides wild oats?`,
        subtitle: $localize`If yes, please specify which weed types.`,
        type: 'textarea',
      },
    ],
    [
      {
        id: 'aspects',
        title: $localize`How important are the following aspects in a tool for detecting and logging wild oats?`,
        subtitle: $localize`Select the level of importance for each object `,
        type: 'radio-block',
        values: [
          { id: 'not-important', label: $localize`Not important` },
          { id: 'important', label: $localize`Moderately important` },
          { id: 'very-important', label: $localize`Very important` },
        ],
        options: [
          { id: 'accuracy', label: $localize`Accuracy in identifying and logging wild oats` },
          { id: 'alerts', label: $localize`Real-time alerts during harvest` },
          { id: 'comparison', label: $localize`Comparison of weed data over time` },
          { id: 'combine-terminal', label: $localize`Available in combine terminal` },
          { id: 'tractor-terminal', label: $localize`Available in tractor terminal` },
          { id: 'compatibility', label: $localize`Compatibility with farm management systems` },
          { id: 'map', label: $localize`Creation of application map for spot spraying based on wild oat detection` },
        ],
      },
    ],
    [
      {
        id: 'anything-else',
        title: $localize`Anything else you'd like to share`,
        subtitle: $localize`Please add add any other comments, ideas and change requests, you would like to share with us`,
        type: 'textarea',
      },
      {
        id: 'title',
        title: $localize`E-mail contact`,
        subtitle: $localize`In case we need more information, we would like to contact you on the e-mail address you gave us when signing up.`,
        type: 'consent',
        options: [
          { id: 'mail', label: $localize`By ticking this box you allow us to get in contact with you by e-mail.` },
        ],
      },
    ],
  ],
};
